import type { Degree, Distance, Margins, Rectangle } from "@pdfking/math"
import { ZERO_DEGREE } from "@pdfking/math/constants"
import type { LayerID } from "@pdfking/renderer-api"
import type { GrowTemplate } from "@pdfking/templates"
import { Keys } from "@pdfking/tfunc"

import type { GrowLeaf, GrowSize, OrientationKind, ResolvedGrowNode } from "#internal"
import type { Drawing, GrowData, ImpositionLayout, ImpositionObject, PageMeta } from "#internal"

import {
  CUT_LAYER_ID,
  FILE_LAYER_ID,
  FOLD_LAYER_ID,
  HELP_LAYER_ID,
  NAME_LAYER_ID,
} from "../../../data/LayerList"
import { CreateGroup } from "../../../shapes/CreateGroup"
import { CreateExtraShapes } from "./CreateExtraShapes"
import { DrawGrowCut } from "./DrawGrowCut"
import { DrawGrowFile } from "./DrawGrowFile"
import { DrawGrowFold } from "./DrawGrowFold"
import { DrawGrowHelp } from "./DrawGrowHelp"
import { DrawGrowName } from "./DrawGrowName"
import { GrowSideSize } from "./GrowSideSize"
import { GrowSignature } from "./GrowSignature"
import { ResolveGrow } from "./ResolveGrow"

export type GrowLayoutInput = {
  /** The page, the way round the orientation wants it. */
  canvas: Rectangle<Distance>
  orientation: OrientationKind
  grow: GrowTemplate
  dimensions: Pick<GrowSize, "depth" | "sheet_depth">
  padding: Margins<Distance>
  security: Margins<Distance>
  title?: string
  print_front: boolean
  print_rear: boolean
  box_opts?: GrowSize
}

const drawing = (layer: LayerID, draw: Drawing["draw"]): Drawing => ({
  layer,
  draw,
  rotation: ZERO_DEGREE,
})

// Drawn on the sheet where things landed, turned as they are: nothing to turn
// later.
const on_page = (layer: LayerID, draw: Drawing["draw"]): Drawing => ({
  layer,
  draw,
  rotation: undefined,
})

/**
 * A component's grow as a layout: the page folded, cut and numbered the way
 * its grow kind says, ready to be tiled onto a machine size or to stand as a
 * sheet of its own.
 *
 * A single-page grow — a fold, a cover, a box — is one page the size of the
 * whole grow; its cells are drawn on it once it has landed. A book is a grid of
 * pages, one per cell, row by row from the top, each exactly the page's size.
 *
 * Every drawing the grow needs is hung here: the media on the page; the
 * names, folds, cut, help marks and die-lines on the grow. Nothing is placed
 * here: they are all read off where the pages landed ({@link GrowGuides}).
 */
export const BuildGrowLayout = (input: GrowLayoutInput): ImpositionObject => {
  const { canvas, orientation, grow } = input
  const resolved = ResolveGrow(
    grow.layout,
    GrowSideSize(canvas, orientation, input.dimensions),
    orientation.opening_top
  )
  const { size, leaves } = resolved

  // One page the size of the whole grow — a fold, a cover, a box — rather
  // than a page per piece.
  const collapsed = grow.is_single_page

  const data: GrowData = {
    grow,
    leaves,
    collapsed,
    padding: input.padding,
    security: input.security,
    title: input.title,
    subtitle: undefined,
    print_front: input.print_front,
    print_rear: input.print_rear,
    box_opts: input.box_opts,
  }

  const extra = CreateExtraShapes(grow.id, input.box_opts, orientation.opening_top)
  // Folds, cut and help marks read where the pages landed, on the sheet; the
  // die-lines of a box are drawn in the grow's own frame and turn with it.
  // A die drawing its own folds is folded there, not between its pages.
  const grow_drawings: Drawing[] = [
    on_page(CUT_LAYER_ID, DrawGrowCut),
    ...(extra[FOLD_LAYER_ID] ? [] : [on_page(FOLD_LAYER_ID, DrawGrowFold)]),
    on_page(HELP_LAYER_ID, DrawGrowHelp),
    on_page(NAME_LAYER_ID, DrawGrowName),
    ...Keys(extra).map(layer =>
      drawing(layer as LayerID, (_node, frame) =>
        CreateGroup([...(extra[layer as LayerID] ?? [])], {
          translate: { x: frame.inner.x, y: frame.inner.y },
        })
      )
    ),
  ]

  // A page of the grow: one per piece, or — collapsed — one for the whole
  // grow, no piece of its own, standing upright.
  const page = (index: number, leaf: GrowLeaf | undefined): ImpositionLayout => {
    // A book's page is numbered within its signature, the signatures of a
    // grow that holds more than one each a group of their own
    const signature = grow.is_book
      ? GrowSignature(grow.sides_tuple, leaf?.meta.page_front ?? leaf?.meta.page_rear)
      : undefined
    const meta: PageMeta = {
      print_front: input.print_front,
      print_rear: input.print_rear,
      page_index: index + 1,
      page_total: leaf ? (signature?.pages ?? leaves.length) : 1,
      page_group_id: signature && grow.sides_tuple.length > 1 ? String(signature.index) : undefined,
      leaf,
      order_front: leaf ? leaf.meta.page_front : 0,
      order_rear: leaf ? leaf.meta.page_rear : 1,
      flip_margins: leaf?.flip,
      renderer: grow.renderer,
    }
    return {
      enum: "page",
      page_number: index + 1,
      size: leaf ? { width: leaf.box.width, height: leaf.box.height } : size,
      rotation: leaf ? (leaf.meta.rotation as Degree) : ZERO_DEGREE,
      ...(leaf ? { padding: leaf.padding } : {}),
      data: {
        drawings: [on_page(FILE_LAYER_ID, DrawGrowFile)],
        page: meta,
        grow: undefined,
        fill: undefined,
      },
    }
  }

  // The tree as the grow stacks it: a pair is folded together and stays
  // packed; the room the fill leaves over goes between the pairs and between
  // the rows as it goes between the grows, a gap each — none under a compact
  // fill.
  const indexed = new Map(leaves.map((leaf, i) => [leaf, i]))
  const stack = (node: ResolvedGrowNode): ImpositionLayout =>
    node.enum === "leaf"
      ? page(indexed.get(node.leaf)!, node.leaf)
      : {
          enum: "flex",
          direction: node.axis === "x" ? "row" : "column",
          ...(node.compact ? { fill_mode: "compact" as const } : {}),
          layouts: node.children.map(stack),
        }
  const layouts: ImpositionLayout[] = collapsed ? [page(0, undefined)] : [stack(resolved.root)]

  return {
    is_front: true,
    size,
    layout: {
      enum: "flex",
      direction: "column",
      layouts,
      data: { drawings: grow_drawings, page: undefined, grow: data, fill: undefined },
    },
  }
}
